import type { APIFormatThemeFontsData } from "containers/admin/clients/client/theme-page/preview/preview.types";
import type { GoogleFont } from "containers/admin/clients/client/theme-page/preview/sub-tabs/fonts/google-fonts/google-fonts.types";
import type { TypekitFamily } from "types";
import type { BeeFont } from "types/bee";

import type {
  FontFamilyPreview,
  FontsFormValues,
} from "containers/admin/clients/client/theme-configuration-page/brand-identity/fonts-tab/fonts-tab.types";

import {
  BEE_FONT_WEIGHT_LABELS,
  getTypekitStylesheetUrl,
} from "containers/admin/clients/client/theme-configuration-page/brand-identity/fonts-tab/fonts-tab.constants";

const GOOGLE_FONTS_CSS_URL = "https://fonts.googleapis.com/css2";

const GOOGLE_FALLBACK_STACKS: Record<string, string> = {
  "sans-serif": "Helvetica, Arial, sans-serif",
  serif: "Georgia, Times, serif",
  monospace: "Courier New, monospace",
};

export const fontsToFormValues = (fonts?: APIFormatThemeFontsData): FontsFormValues => ({
  adobeFontsProjectId: fonts?.adobeFontsProjectId ?? "",
  adobeFontsDraft: fonts?.adobeFontsProjectId ?? "",
  googleFonts: (fonts?.googleFonts?.length ? fonts.googleFonts : [""]).map((family) => ({ family })),
});

/** Google lists upright weights as `"regular"` or a bare number; every italic carries `italic`. */
const getGoogleFontWeights = (variants: string[]) =>
  Array.from(
    new Set(
      variants
        .filter((variant) => variant === "regular" || /^\d{3}$/.test(variant))
        .map((variant) => (variant === "regular" ? 400 : Number(variant))),
    ),
  ).sort((a, b) => a - b);

/** Typekit variations are FVD codes: `n4` is upright 400, `i7` italic 700. */
const getAdobeFontWeights = (variations: string[] = []) =>
  Array.from(
    new Set(
      variations
        .filter((variation) => variation.startsWith("n"))
        .map((variation) => Number(variation.slice(1)) * 100),
    ),
  ).sort((a, b) => a - b);

/** An italic-only family has no upright weight to request, so the axis is left for Google to default. */
const getGoogleStylesheetUrl = (family: string, weights: number[]) => {
  const familyParam = encodeURIComponent(family).replace(/%20/g, "+");
  const weightAxis = weights.length ? `:wght@${weights.join(";")}` : "";

  return `${GOOGLE_FONTS_CSS_URL}?family=${familyParam}${weightAxis}&display=swap`;
};

/**
 * A kit lists one family per CSS name it serves, and the same face can appear more than once; the
 * duplicates are merged so each family is previewed and handed to BEE once, with every weight it has.
 */
const getAdobeFamilies = (families: TypekitFamily[]) =>
  Object.values(
    families
      .filter((family) => family.name && family.slug && family.css_stack && family.css_names?.[0])
      .reduce<Record<string, TypekitFamily>>((byCssName, family) => {
        const key = family.css_names![0];
        const existing = byCssName[key];

        byCssName[key] = existing
          ? { ...existing, variations: [...(existing.variations ?? []), ...(family.variations ?? [])] }
          : family;

        return byCssName;
      }, {}),
  );

export const getGoogleFontPreviews = (
  selectedFamilies: string[],
  googleFonts: GoogleFont[],
): FontFamilyPreview[] =>
  selectedFamilies
    .map((family) => googleFonts.find((font) => font.family === family))
    .filter((font): font is GoogleFont => !!font)
    .map(({ family, variants }) => {
      const weights = getGoogleFontWeights(variants);

      return {
        name: family,
        source: "google",
        cssFontFamily: `'${family}'`,
        weights,
        stylesheetUrl: getGoogleStylesheetUrl(family, weights),
      };
    });

export const getAdobeFontPreviews = (
  projectId: string,
  families: TypekitFamily[],
): FontFamilyPreview[] =>
  getAdobeFamilies(families).map((family) => ({
    name: family.name!,
    source: "adobe",
    cssFontFamily: family.css_stack!,
    weights: getAdobeFontWeights(family.variations),
    stylesheetUrl: getTypekitStylesheetUrl(projectId),
  }));

const toBeeWeights = (weights: number[]) =>
  Object.fromEntries(weights.map((weight) => [weight, BEE_FONT_WEIGHT_LABELS[weight]]));

/**
 * The `fonts` section the legacy theme page and the BEE config already read, `beeFonts` included —
 * built the way the legacy save builds it, so either page can save fonts without breaking the other.
 *
 * `adobeFamilies` is `undefined` while the kit is unknown (still loading, or Typekit failed) and
 * `googleFontList` may not list a saved family yet. In both cases the entry already saved in
 * `previousBeeFonts` is kept, so an unrelated save never drops a font from the email editor.
 */
export const toSavedFonts = (
  { adobeFontsProjectId, googleFonts }: FontsFormValues,
  googleFontList: GoogleFont[],
  adobeFamilies: TypekitFamily[] | undefined,
  previousBeeFonts: BeeFont[] = [],
): APIFormatThemeFontsData => {
  const families = googleFonts.map(({ family }) => family).filter(Boolean);
  const typekitStylesheetUrl = getTypekitStylesheetUrl(adobeFontsProjectId);

  const getBeeAdobeFonts = (): BeeFont[] => {
    if (!adobeFontsProjectId) {
      return [];
    }

    if (!adobeFamilies) {
      return previousBeeFonts.filter((font) => font.url === typekitStylesheetUrl);
    }

    return getAdobeFamilies(adobeFamilies).map((family) => ({
      name: family.name!,
      fontFamily: family.css_stack!.replace(/^"([^"]+)"(,?)/, "$1$2"),
      value: family.slug,
      url: typekitStylesheetUrl,
      fontWeight: toBeeWeights(getAdobeFontWeights(family.variations)),
    }));
  };

  const toBeeGoogleFont = (family: string): BeeFont | undefined => {
    const font = googleFontList.find((googleFont) => googleFont.family === family);

    if (!font) {
      return previousBeeFonts.find(
        (previous) => previous.value === family && previous.url.startsWith(GOOGLE_FONTS_CSS_URL),
      );
    }

    const weights = getGoogleFontWeights(font.variants);

    return {
      name: family,
      fontFamily: `'${family}', ${GOOGLE_FALLBACK_STACKS[font.category] ?? "sans-serif"}`,
      value: family,
      url: getGoogleStylesheetUrl(family, weights),
      fontWeight: toBeeWeights(weights),
    };
  };

  return {
    googleFonts: families,
    adobeFontsProjectId,
    beeFonts: [
      ...getBeeAdobeFonts(),
      ...families.map(toBeeGoogleFont).filter((font): font is BeeFont => !!font),
    ],
  };
};
